import React, { lazy, Suspense } from 'react';
import {
  Route,
  Redirect,
  BrowserRouter as Router,
  Switch,
} from 'react-router-dom';
import { useSelector } from 'react-redux';

import ErrorBoundary from './ErrorBoundary';
import { TourProvider } from '@components/Tour/TourContext';
import TourOverlay from '@components/Tour/TourOverlay';
import { MockProvider } from '@components/MockProvider/MockContext';
import { publicRoutes, privateRoutes, mobileRoutes } from './route.constants';
import Loader from '@components/Loader/Loader';
import ContentLoader from '@components/Layout/ContentLoader/ContentLoader';
import LayoutWrapper from '@components/Layout/Content/LayoutWrapper';
import NotAllowedScreen from './screens/NotAllowed';
import { useIsMobile } from './hooks/useIsMobile';

const responsiveRoutes = privateRoutes.filter(r => r.isMobile);
const desktopOnlyRoutes = privateRoutes.filter(r => !r.isMobile);

const AuthGuard = ({ children }) => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  if (!isLoggedIn) {
    return <Redirect to="/signin" />;
  }

  return children;
};

const MobileGuard = ({ children }) => {
  const isMobile = useIsMobile();
  const view = useSelector(state => state.App.view);

  if (isMobile || view === 'MobileView') {
    return <Redirect to="/mobile" />;
  }

  return children;
};

const CompanyGuard = ({ children, route }) => {
  const user = useSelector(state => state.auth.user);
  const companies = useSelector(state => state.company.companies);
  const companiesUser = useSelector(state => state.company.companiesUser);
  const selectedCompany = useSelector(state => state.company.selectedCompany);

  if (route?.path === '/mobile/select-company') {
    return children;
  }

  if (selectedCompany) {
    return children;
  }

  const availableCompanies = companiesUser;

  if (availableCompanies.length === 0) {
    return <Loader />;
  }

  if (availableCompanies.length > 1) {
    return <Redirect to="/mobile/select-company" />;
  }

  return children;
};

const MobileRoute = ({ route }) => {
  const user = useSelector(state => state.auth.user);
  const hasAbility = route.ability === null ||
    user?.abilities?.some(a => a.ability === route.ability) ||
    user?.isAdmin;

  return hasAbility ? <route.component /> : <NotAllowedScreen />;
};

const ResponsiveRoute = ({ route }) => {
  const isMobile = useIsMobile();
  const view = useSelector(state => state.App.view);
  const user = useSelector(state => state.auth.user);

  const hasAbility = route.ability === null ||
    user?.abilities?.some(a => a.ability === route.ability) ||
    user?.isAdmin;

  if (!hasAbility) return <NotAllowedScreen />;

  const isDeviceMobile = isMobile || view === 'MobileView';

  if (isDeviceMobile && route.mobileComponent) {
    return (
      <CompanyGuard route={route}>
        <Suspense fallback={<Loader />}>
          <route.mobileComponent />
        </Suspense>
      </CompanyGuard>
    );
  }

  return (
    <LayoutWrapper>
      <Suspense fallback={<ContentLoader />}>
        <route.component />
      </Suspense>
    </LayoutWrapper>
  );
};

const DashboardLayout = () => {
  const user = useSelector(state => state.auth.user);

  return (
    <LayoutWrapper>
      <Suspense fallback={<ContentLoader />}>
        <Switch>
          {desktopOnlyRoutes.map((route, index) => {
            const hasAbility = route.ability === null ||
              user?.abilities?.some(a => a.ability === route.ability) ||
              user?.isAdmin;

            return (
              <Route key={index} path={route.path} exact={route.exact}>
                {hasAbility ? <route.component /> : <NotAllowedScreen />}
              </Route>
            );
          })}
          <Route component={lazy(() => import('@screens/404/404'))} />
        </Switch>
      </Suspense>
    </LayoutWrapper>
  );
};

export default function Routes() {
  return (
    <ErrorBoundary>
      <MockProvider>
      <TourProvider>
      <Router>
        <Switch>
          {publicRoutes.map((route, index) => (
            <Route key={index} path={route.path} exact={route.exact}>
              <Suspense fallback={<Loader />}>
                <route.component />
              </Suspense>
            </Route>
          ))}

          {mobileRoutes.map((route, index) => (
            <Route key={`mobile-${index}`} path={route.path} exact={route.exact}>
              <AuthGuard>
                <CompanyGuard route={route}>
                  <Suspense fallback={<Loader />}>
                    <MobileRoute route={route} />
                  </Suspense>
                </CompanyGuard>
              </AuthGuard>
            </Route>
          ))}

          <Redirect exact from="/campaign-management" to="/configuracao-campanhas" />

          {responsiveRoutes.map((route, index) => (
            <Route key={`responsive-${index}`} path={route.path} exact={route.exact}>
              <AuthGuard>
                <ResponsiveRoute route={route} />
              </AuthGuard>
            </Route>
          ))}

          <Route path="/">
            <AuthGuard>
              <MobileGuard>
                <DashboardLayout />
              </MobileGuard>
            </AuthGuard>
          </Route>
        </Switch>
        <TourOverlay />
      </Router>
      </TourProvider>
      </MockProvider>
    </ErrorBoundary>
  );
}
